import { TELL_O_MESSAGES } from "@CommonUtils/pog/tellO/messages";

export const commonEn = {
  storeProducts: {
    title: "Store {{code}} Products",
    genericTitle: "Store Products",
    loadError: "Unable to load the store details. Please try again.",
    loading: "Loading store details...",
  },
  authVerificationUnavailable: "Unable to verify your session. Check your connection and retry.",
  authVerifying: "Verifying your session…",
  authRetryVerification: "Retry session verification",
  reviewSelected: "Review selected",
  reviewSelectedCount: "Review selected ({{count}})",
  reviewPlanograms: "Review planograms",

  na: "N/A",
  itemSwappedOut: "Item Swapped Out:",
  itemSwappedIn: "Item Swapped In:",
  facingsChange: "Facings:",
  swapFacingsModeLabel: "Facings Mode",
  swapFacingsModeMatch: "Match swap-out facings",
  swapFacingsModeFree: "Let DOS balancing decide",
  multiplePlacementFailures: "Multiple placement failures",
  updateReport: {
    skipped: "SKIPPED",
    skipReasons: {
      PRODUCT_NOT_IN_POG: "Product is not in the target planogram",
      ALREADY_AT_TARGET: "Already at target facings",
      ABOVE_EXPANSION_TARGET: "Current facings already exceed the expansion target",
      BELOW_REDUCTION_TARGET: "Current facings are already below the reduction target",
      UNKNOWN: "Operation skipped",
    },
    skippedFacings: "Current facings: {{current}} · Requested target: {{target}}",
    facingsExpanded: "FACINGS EXPANDED",
    facingsReduced: "FACINGS REDUCED",
    item: "ITEM",
    items: "ITEMS",
  },
  search: "Search",
  close: "Close",
  cancel: "Cancel",
  save: "Save",
  delete: "Delete",
  edit: "Edit",
  view: "View",
  back: "Back",
  next: "Next",
  loading: "Loading...",
  noResults: "No results found",
  multiSelect: {
    selectedCount: "{{count}} selected",
  },
  pogWarning: {
    rangeRecommendation: "Range recommendation.",
    restrictionNotApplied:
      "Rule not applied: {{rule}}. {{cdtGroup}} also holds products the rule doesn't name.",
    relaxedToFinish: {
      label: "Relaxed to finish",
      intro: "The solve could not finish under every rule, so the pogger relaxed these, in order:",
      rung: "{{index}}. {{rung}}: {{rules}}",
      after: "after {{error}}",
      rules: {
        placeAllCdt1s: "place every CDT1 group",
        placeAllVariants: "place every product variant",
        strictRectangles: "keep product blocks rectangular",
        mainSolve: "main solve",
        generatedMudmap: "generated mudmap",
        other: "an additional solve detail",
      },
      errors: {
        infeasible: "the solver could not find a feasible planogram",
        timeout: "the solver ran out of time",
        other: "the previous solve could not complete",
      },
      rungs: {
        other: "Additional relaxation",
        fallback_place_all: "Room for every CDT1 and variant",
        fallback_variant_fences: "Shelf variant fences",
        fallback_block_shapes: "Block shapes",
        fallback_mudmap: "Generated mudmap",
        fallback_greedy_fill: "Main solve (greedy fill)",
        fallback_cp_model_presolve: "Solver presolve",
      },
    },
  },
  merchflow: {
    saveFailed: "Failed to save merchflow",
    editName: "Edit merchflow name",
    editGoLiveDate: "Edit Go-Live Date",
    goLiveDate: "Go-Live Date",
    deactivationDate: "Deactivation Date",
  },
  storePogNotes: {
    button: "Notes",
    title: "Store POG {{id}} notes",
    field: "Notes",
    noNotes: "No notes saved yet",
    lastEdited: "Last edited on {{date}}",
    lastEditedBy: "Last edited by {{name}} on {{date}}",
    loadFailed: "Failed to load notes.",
    saveFailed: "Failed to save POG notes",
    discardTitle: "Discard Unsaved Changes?",
    discardDescription:
      "Are you sure you want to discard your changes? All unsaved notes will be permanently lost.",
    continueEditing: "Continue Editing",
    discardChanges: "Discard Changes",
  },
  storePogExport: {
    forceOverwritePdfs: "Force overwrite PDFs",
    latestVersionNote: "Exports each store's latest planogram, not the version shown.",
    approvedVersionNote:
      "Exports the approved planogram for each store, whichever version was approved.",
    csvColumns: {
      title: "Choose CSV columns",
      rangedItems: "Ranged Items",
      unrangedItems: "Unranged Items",
      loading: "Loading columns...",
      loadFailed: "CSV columns could not be loaded. Try again.",
      loadFailedToast: "Failed to load CSV export columns",
      retry: "Retry",
      search: "Search columns",
      selectedCount: "{{selected}} of {{total}} columns selected",
      noMatches: "No columns match your search.",
      export: "Export CSV",
    },
  },
  download: "Download",
  generate: "Generate",
  status: "Status",
  category: "Category",
  store: "Store",
  stores: "Stores",
  cluster: "Cluster",
  brand: "Brand",
  critical: "Critical",
  warning: "Warning",
  medium: "Medium",
  low: "Low",
  active: "Active",
  shelves: "Shelves",
  start: "Start: {{date}}",
  end: "End: {{date}}",
  alerts: "Alerts ({{count}})",
  before: "Before",
  after: "After",
  change: "Change",
  current: "Current",
  salesValue: "Sales Value",
  unitsSold: "Units Sold",
  grossMargin: "Gross Margin",
  inventoryTurnover: "Inventory Turnover",
  daysOfInventory: "Days of Inventory",
  outOfStock: "Out of Stock (%)",
  pareto: "Pareto 80/20",
  premium: "Premium",
  urban: "Urban",
  suburban: "Suburban",
  language: "Language",
  shelf: "Shelf",
  selectedShelves: "Selected Shelves",
  selectedShelf: "Selected Shelf",
  shelvesCount: "{{count}} shelves",
  shelfName: "Shelf: {{name}}",
  shelfNotchPrefix: " N:{{notch}}",
  planogramFitCheck: {
    singleStackTitle: "Stack is taller than its shelf allows",
    multipleStacksTitle: "{{count}} stacks are taller than their shelves allow",
    rowFits: "{{item}}: {{count}} row fits",
    rowsFit: "{{item}}: {{count}} rows fit",
    more: "and {{count}} more",
  },
  shelfEditor: {
    label: "Shelf editor",
  },
  shelfTypes: {
    label: "Shelf type",
    regular: "Regular",
    riser: "Riser",
    hangCell: "Hang Cell",
    setFailed: "Failed to set shelf type",
  },
  // Weight vocabulary: a board's maximum load is its LIMIT, never its
  // "capacity" — capacity already means units on this screen.
  planogramRules: {
    blocking: {
      summary: "Primary blocking: {{mode}}, using {{cdtSource}}.",
      vertical: "vertical",
      horizontal: "horizontal",
      followsStudioOn: "Follows CDT Studio: on, using release {{releaseId}}.",
      followsStudioOffRows:
        "Follows CDT Studio: off, release {{releaseId}} uses horizontal blocking on this wall.",
      followsStudioOffNotFinalized: "Follows CDT Studio: off until CDTs are finalized there.",
      verticalBlockedTitle: "Vertical blocking needs finalized CDTs",
      verticalBlockedText:
        "CDT Studio was not finalized for this planogram, so a vertical run cannot start.",
      verticalBlockedReason:
        "Finalize CDTs in CDT Studio or switch to horizontal blocking to generate.",
      stayHorizontal: "Stay horizontal",
      openStudio: "Open CDT Studio",
      /* Finalize is mandatory where the Studio applies: no store generation before it. */
      finalizeFirstTitle: "Finalize CDTs first",
      finalizeFirst:
        "Finalize CDTs first: store planograms are built from the rules the Studio ships.",
    },
    mudmapWeights: {
      title: "Mudmap weights",
      reset: "Reset Mudmap Weights",
      resetConfirmTitle: "Reset Mudmap Weights?",
      resetConfirmDescription:
        "Are you sure that you want to reset all mudmap weights to their defaults?",
      keepCurrentValues: "Keep Current Values",
      resetToDefaults: "Reset to Defaults",
    },
    fingerSpace: {
      title: "Finger space (inches)",
      description:
        "Gap kept between the top of the products and the shelf above, on every shelf in this category. Relaxed per shelf only when it cannot be kept; how strongly is set by Finger space — relaxation on the Optimization tab.",
    },
    snakingCascade: {
      title: "Snaking cascade level",
      description: "Align bay-spanning variants by cascade snaking",
      off: "Off",
    },
    frontFacings: {
      title: "How strongly do we prefer front facings?",
      frontOnly: "Front facings only",
      turnedWhereAllowed:
        "Front facings first; a pack may stand turned where the product allows it",
    },
    fillExtraSpace: {
      title: "Can we fill extra space next to cases / trays with loose products?",
      description:
        "Also turns on side caps (units turned on their side beside the facings) and top caps (units laid on their side across the top of the facings) on every wall, as well as loose units next to trays. Caps go only where there is room left that a whole facing can't use, and never on pallets.",
      // Flow and simulation updates run frogger, not pogger: caps as before.
      updateDescription:
        "Also turns on caps: side caps (a pack turned on its side next to its block) and top caps (a pack laid flat on top of its stack), as well as loose units next to trays. Caps go only where there is room left that a whole facing or row can't use, and never on pallets.",
    },
    stackToShelfTop: {
      title: "Fill high to the shelf above",
      description:
        "Add facings high until each product reaches the shelf above, not just the height of the products beside it.",
    },
    stackPastMaxStack: {
      title: "Ignore max high",
      description: "Let facings high go past each product's max high.",
    },
    verticalCapping: {
      title: "Top capping",
      description:
        "Lay units on their side across the top of the facings, in the space left under the shelf above.",
    },
    verticalCappingPastMaxStack: {
      title: "Top cap past max high",
      description:
        "Also top-cap products with a max high of 1, and facings with room for another unit high.",
    },
    slowMovers: {
      title: "Leave slow movers out of the planogram?",
      description:
        "Products selling fewer units a week than the floor below are not sent to the planogram engine at all. Core range products and items added to the store in the last 3 months always stay in.",
      threshold: "Minimum units sold per week",
      badge: "Slow mover",
    },
    optimizationOptions: {
      fingerSpaceTitle: "Finger space — relaxation",
      fingerSpace:
        "How strongly the finger space gap set on the General tab is kept. 0 lets any shelf drop it; ∞ never does. Not the gap itself.",
      fixedNotchNum:
        "Weight for keeping each shelf at its current notch. 0 lets every shelf move freely; ∞ pins them all. Turning off shelf-notch adjustment on the General tab pins them regardless of this slider.",
      fixedNotchNumPinned:
        "Pinned to ∞ for this run: shelf notches cannot be adjusted (General › Fixtures), so store generation ignores this slider.",
      cdtOrder:
        "How strictly CDT {{level}} ordering is enforced. ∞ means never relax it — and when the planogram is built from an existing mudmap that already breaks CDT {{level}} order, the run is rejected before solving rather than relaxed.",
      prereq:
        "How strictly product prerequisites are enforced. Defaults to ∞ (never relaxed), so every run already ships one constraint the solver is not allowed to break.",
      minDistribution:
        "Weight on each required core-range product left off the planogram. A plain number, unlike its neighbours: there is no ∞ here, so 100 is as strict as it goes. Store generation sends 10 in place of 0.",
    },
    uhd: {
      verticalSplitDescription:
        "Each extra split multiplies the run's time limit: 5 splits is a run five times longer, half of it spent building the mudmap.",
      brandBlocks: {
        title: "Brands get their own blocks from",
        description:
          "With vertical blocking, stores at or above this bay count plan rectangular brand blocks. Final edges can vary.",
        descriptionNever: "With vertical blocking, no store uses rectangular brand planning.",
        oneBay: "1 bay",
        bays: "{{count}} bays",
        never: "Never",
      },
    },
    pallets: {
      title: "Pallets",
      onlyPalletStores: "Only stores with floor pallets use these.",
      maxProducts: "Most products on one pallet",
      maxStackHeight: "Max stack height (in)",
      maxStackHeightHelp:
        "0 stacks each pallet to its own fixture height. A pallet never stacks above its fixture.",
    },
    weightRules: {
      tab: "Weight",
      shelfCap: {
        title: "Shelf load limit",
        limit: "Maximum load per shelf (grams)",
        pounds: "{{pounds}} lb",
        noCap: "No cap — shelves are reported but not judged",
        limitRequired: "Required while this is on — enter a load limit above zero.",
      },
      heavyRule: {
        title: "Heavy items sit low",
        threshold: "Heavy at or above (grams)",
        thresholdPounds: "{{pounds}} lb",
        maxDeckY: "Height line",
        maxDeckYHelp:
          "A coordinate in the same scale as your fixture geometry, read off the planogram. Not millimetres, not inches — the same numbers your bays and shelves already use.",
        maxDeckYRequired:
          "Required — there is no safe default. Read this off your own fixtures; this rule stays off until you enter one.",
      },
      passes: {
        resolveTitle: "Overweight shelves",
        resolve: "Resolve overweight shelves",
        resolveHelp:
          "Trim depth on shelves over the cap, never below an item's supply floor; then delist the worst value-per-weight product until the shelf is under its cap.",
        swapTitle: "Heavy brand placement",
        swap: "Swap heavy brands low",
        swapHelp:
          "Exchange whole brand blocks between a high and a low shelf so heavy product sits below the height line, keeping each brand contiguous.",
        swapNeedsHeavyRule: 'Turn on "Heavy items sit low" to give this rule its numbers.',
      },
      advanced: {
        title: "Advanced",
        fillFloor: "Minimum fill kept after a swap",
      },
    },
    // The Dividers section of the rules modal (dividers and spring loaders).
    dividers: {
      title: "Dividers",
      change: "Change",
      done: "Done",
      heading: "Products that get dividers or spring loaders",
      explanation:
        "A shelf gets dividers, or a spring loader, when more than half its facings are ticked for it; then every facing on it gets one.",
      badData:
        "The base planogram's divider sizes can't be used. Tick the products that get dividers and enter a width.",
      badDataTitle: "The base planogram's divider data can't be used.",
      dividerWidth: "Divider width",
      loaderDepth: "Spring loader depth",
      fromBase: "From the base planogram.",
      fromFixtures: "From the Fixtures page.",
      fromBoth: "From the base planogram and the Fixtures page.",
      tickCounts: "Dividers: {{dividers}} of {{total}} · Spring loaders: {{loaders}} of {{total}}",
      resetToBase: "Reset to base planogram",
      clearAll: "Clear all",
      groupBy: "Group by",
      searchProducts: "Search products",
      columnDividers: "Dividers",
      columnLoaders: "Spring loader",
      products: "Products",
      noGroup: "No group",
      showGroup: "Show {{name}}",
      hideGroup: "Hide {{name}}",
      groupCounts: "{{counts}} of {{total}}",
      loadingProducts: "Loading products…",
      noProducts: "No products",
      onBase: "On the base planogram",
      readingBase: "Reading the base planogram…",
      noShelves: "No shelves in the base planogram",
      keepFixture: "Keep dividers that store fixtures already have.",
      ends: "Ends of the shelf",
      atStart: "Divider at the left end",
      atEnd: "Divider at the right end",
      loaderMode: "Spring loaders",
      loaderModes: {
        products: "Follow the products",
        shelf: "Stay with the shelf",
      },
      shelfModeNote: "Ticks only mark which products need one.",
      explanationShelfMode:
        "A shelf gets dividers when more than half its facings are ticked for them; spring loaders stay where the shelf hardware is.",
      shelfHardwareDecides: "(shelf hardware decides)",
      errors: {
        widthMissing: "Enter the divider width (the thickness of one divider).",
        loaderDepthMissing: "Enter the spring loader depth.",
        loaderDepthNegative: "Enter 0 or more.",
        loaderDepthNotBelowShelf: "Must be less than the shelf depth ({{depth}}).",
        loaderDepthNotBelowShallowestShelf: "Must be less than the shallowest shelf ({{depth}}).",
        ruleInvalid: "The divider rule could not be read.",
      },
      oSummary: "O summary",
      level: "Level {{level}}",
      // O's sentence about dividers; {{keep}} is keepClause with a leading space, or empty.
      o: {
        reading: "O is reading the base planogram…",
        noBase: "O couldn't read the base planogram, so no dividers.{{keep}}",
        badBaseOneFault:
          "The base planogram's {{faults}} (an import fault), so O set no dividers.{{keep}}",
        badBaseFaults:
          "The base planogram's {{faults}} (import faults), so O set no dividers.{{keep}}",
        faultWidth: "divider width equals the shelf width on {{shelves}}",
        faultFlags: "divider start and end switches read above 1 on {{shelves}}",
        faultsJoin: "{{first}} and {{second}}",
        oneShelf: "{{count}} shelf",
        shelves: "{{count}} shelves",
        noneInBase: "No dividers in the base planogram, so none here.{{keep}}",
        noneOnAnyShelf: "No dividers on any shelf.",
        noneNeededNoFixtures:
          "No products need dividers. O can't see this store's fixtures, so none are kept.",
        noneNeededKept:
          "No products need dividers. {{kept}} of {{total}} shelves keep the dividers their fixtures already have.",
        noneNeededKeptWithWidth:
          "No products need dividers. {{kept}} of {{total}} shelves keep the dividers their fixtures already have, {{width}}.",
        noWidthYet: "Dividers for {{products}}, but no width yet.{{keep}}",
        likeBase:
          "O set dividers like the base planogram: {{products}}, {{shelves}}, {{width}}.{{keep}}",
        forProducts: "Dividers for {{products}}, on {{shelves}}, {{width}}.{{keep}}",
        keepClause: " Shelves whose fixtures already have dividers keep them.",
        productCount: "{{count}} of {{total}} products",
        shelfCount: "{{count}} of {{total}} shelves",
        width: "{{width}} wide",
      },
      // O's sentence about spring loaders, after the dividers one.
      loaderO: {
        noneInBase: "No spring loaders in the base planogram, so none here.",
        none: "No spring loaders.",
        likeBaseDepthNotSet:
          "O set spring loaders like the base planogram: {{products}}, {{shelves}}; depth not set on {{count}} of them.",
        likeBase: "O set spring loaders like the base planogram: {{products}}, {{shelves}}.",
        likeBaseWithDepth:
          "O set spring loaders like the base planogram: {{products}}, {{shelves}}, {{depth}}.",
        noDepthYet: "Spring loaders for {{products}}, but no depth yet.",
        forProducts: "Spring loaders for {{products}}, on {{shelves}}.",
        forProductsWithDepth: "Spring loaders for {{products}}, on {{shelves}}, {{depth}}.",
        depth: "{{depth}} deep",
        depthRange: "{{low}} to {{high}} deep",
        shelfModeNone: "No spring loaders on the base planogram's shelves, so none here.",
        shelfModeBase: "Spring loaders stay on the base planogram's {{shelves}}.",
        shelfModeBaseWithDepth:
          "Spring loaders stay on the base planogram's {{shelves}}, {{depth}}.",
        shelfModeSome: "Spring loaders stay on {{kept}} of {{total}} shelves.",
        shelfModeSomeWithDepth: "Spring loaders stay on {{kept}} of {{total}} shelves, {{depth}}.",
      },
      // One base planogram shelf in the result list, and the list's first line.
      result: {
        name: "Bay {{bay}} Shelf {{shelf}}",
        dividers: "dividers",
        noDividers: "no dividers",
        facings: "{{needing}} of {{facings}} facings",
        needsMoreThanHalf: "{{needing}} of {{facings}} facings (needs more than half)",
        count: "{{needing}} of {{facings}}",
        countNeedsMoreThanHalf: "{{needing}} of {{facings}} (needs more than half)",
        setAlways: "set to always for this store",
        setNever: "set to never for this store",
        keptFromFixture: "kept from the store fixture",
        total: "{{count}} of {{total}} shelves get dividers.",
        totalWithLoaders:
          "{{count}} of {{total}} shelves get dividers; {{loaders}} get spring loaders.",
        noLoader: "no loader",
        loader: "loader {{depth}}",
        loaderOfShelf: "loader {{depth}} (shelf)",
      },
    },

    tellO: {
      copy: TELL_O_MESSAGES,
      link: "Tell O",
      placeholder: "Tell O what to change…",
      send: "Send",
      freeTextDisabledInBaseMode:
        "Free-text requests aren't available while editing the base planogram. Use a suggestion or open Show details.",
      thinking: "O is reading that…",
      offline: "O can't read free text right now. Tap a chip, or open Show details.",
      failed: "O couldn't answer that. Try again later, or tap a chip.",
      notUnderstood: "O didn't follow that. Try one of these, or open Advanced settings.",
      otherStore: "O can only change shelves on store {{storeCode}} here.",
      otherStoreUnknown: "O can only change shelves on this planogram's store here.",
      undo: "Undo",
      backToDefault: "Back to O's default",
      backToDefaultDone: "Back to O's default.",
      showDetails: "Show details",
      hideDetails: "Hide details",
      notSaved: "Not saved. Try again.",
    },
    // The Customer Decision Tree choice. Store Planogram Rules and Review
    // Update share the labels; the descriptions say what each source does in
    // that flow (PspRules.cdt_source and FlowUpdateCdtSource carry the contract).
    cdtSource: {
      sectionTitle: "Customer Decision Tree",
      question: "Which CDT should these planograms follow?",
      storeProduct: {
        label: "Data feed CDT",
        description:
          "Uses the CDT that came with each product in your latest data feed. Products that came without one take the store's CDT. No CDT Studio release is applied, so the restrictions CDT Studio added are left out; restrictions you set yourself still apply.",
        updateDescription:
          "Uses the CDT that came with each product in your latest data feed. Products that came without one take the store's CDT.",
      },
      categoryTree: {
        label: "Primary / pinned CDT",
        description:
          "Each store uses the version pinned to it. Stores without a pin use this planogram's CDT Studio release, or the primary CDT if there is no release.",
      },
      pogSnapshot: {
        label: "As built into this planogram",
        description:
          "Keep the CDT tree this planogram was generated with. Moving this store onto a different tree afterwards is ignored, but edits made inside that same tree are picked up.",
      },
      pinnedStores_one: "{{count}} store pinned",
      pinnedStores_other: "{{count}} stores pinned",
      pinnedVersion_one: "{{count}} store pinned to {{version}}",
      pinnedVersion_other: "{{count}} stores pinned to {{version}}",
      onRelease_one: "{{count}} store on the Studio release",
      onRelease_other: "{{count}} stores on the Studio release",
      onPrimary_one: "{{count}} store on primary{{version}}",
      onPrimary_other: "{{count}} stores on primary{{version}}",
      defaultedToCategoryTree:
        "Defaulted to Primary / pinned CDT because this planogram has a CDT Studio release.",
      defaultedToStoreProduct:
        "Defaulted to Data feed CDT because this planogram has no CDT Studio release yet.",
      defaultedToCategoryTreeFromVersion:
        "Defaulted to Primary / pinned CDT because this flow was started from a CDT version. Each store uses the version pinned to it, or the primary CDT.",
    },
  },
  // A shelf panel's "Why" row for its dividers; the codes come from omni-common's
  // shelfDividerWhy. A legacy rule's reasons are worded from their values; an older pog's
  // stored text is shown as stored.
  shelfDividers: {
    why: {
      setAlways: "Set to always for this store.",
      keptFromFixture: "Kept from the store fixture.",
      setNever: "Set to never for this store.",
      setNeverWithCounts:
        "Set to never for this store. {{needing}} of {{facings}} facings need dividers.",
      notNeeded: "No product here needs dividers",
      needed: "{{needing}} of {{facings}} facings need dividers",
      neededWithNotes: "{{needing}} of {{facings}} facings need dividers ({{notes}})",
      needsMoreThanHalf: "needs more than half",
      valueIs: "{{label}} is {{value}}",
      noValue: "No {{label}}",
    },
    // The shelf panel's divider rows.
    rows: {
      dividers: "Dividers",
      none: "None",
      betweenEveryFacing: "Between every facing",
      count: "{{count}} dividers",
      width: "Divider width",
      ends: "Divider ends",
      space: "Divider space",
      why: "Why",
    },
    ends: {
      both: "Both ends",
      start: "Left only",
      end: "Right only",
      neither: "Neither",
    },
    // A store shelf's divider hand setting, on the shelf panel and the Fixtures page.
    override: {
      title: "This shelf in this store",
      follow: "Follow the rule",
      always: "Always dividers",
      never: "Never dividers",
      saved:
        "Saved for Bay {{bay}} Shelf {{shelf}} in this store. Regenerate the store to refit the products.",
      notSaved: "Not saved. Try again.",
      saveFailed: "Failed to save the shelf's divider setting",
    },
    // The Fixtures page's divider columns.
    columns: {
      width: "Divider width",
      height: "Divider height",
      depth: "Divider depth",
      atStart: "Divider at start",
      atEnd: "Divider at end",
      betweenFacings: "Dividers between facings",
      thisShelf: "Dividers (this shelf)",
    },
  },
  // A shelf panel's "Why" row for its spring loader; the codes come from omni-common's
  // DEV-3677 front overhang: planogram editor, markers and the Fixtures page.
  frontOverhang: {
    fixBeforeApproval: "Fix shelf overhang before approval or export.",
    violation:
      "{{product}} on shelf {{shelf}} needs {{excess}} inches of front overhang. The shelf allows {{allowance}} inches; only one UNIT pack deep on a level, unmerged regular shelf, within 20% of its oriented depth, is permitted.",
    violationFallback:
      "Product {{product}} on shelf {{shelf}} needs front overhang correction before approval or export.",
    needsCorrection: "Front overhang needs correction",
    shelfDetailsLabel: "Front overhang",
    editShelf: {
      label: "Max Front Overhang:",
      help: "Uses the same length unit as shelf depth. Allows one oversized pack deep, capped at 20% of its depth in the chosen orientation. Zero disables overhang in this planogram. Store planograms also require permission on the store's shelf in Fixtures. Changing this value does not change that physical shelf permission. Only level, unmerged regular shelves are eligible.",
    },
    marker: {
      shelfAllowed: "Overhang allowed",
      shelfCheck: "Check overhang",
      shelfDescription:
        "Shelf overhang allowance: {{allowance}}. Each product must stay within 20% of its depth in the chosen orientation, one UNIT pack deep.",
      shelfDescriptionIneligible:
        "Shelf overhang allowance: {{allowance}}. Each product must stay within 20% of its depth in the chosen orientation, one UNIT pack deep. This shelf is not eligible.",
      itemDescriptionValid:
        "Front overhang: {{excess}} ({{percent}}% of oriented depth). Product depth: {{depth}}; usable shelf depth: {{support}}. Within planogram limits.",
      itemDescriptionInvalid:
        "Front overhang: {{excess}} ({{percent}}% of oriented depth). Product depth: {{depth}}; usable shelf depth: {{support}}. Needs correction before approval or export.",
    },
    legend: {
      label: "Front overhang legend",
      shelfAllowed: "Shelf edge: overhang allowed",
      shelfCheck: "Shelf edge: check overhang",
      product: "Product edge and underside: {{label}}",
      productInvalid: "Product edge and underside: {{label}} (needs correction)",
      severalProducts: "front overhang",
    },
    fixtures: {
      header: "Max Front Overhang",
      headerWithUnit: "Max Front Overhang ({{unit}})",
      storeHeader: "Store",
      categoryIdHeader: "Category ID",
      storeCode: "Store code",
      allStores: "All stores",
      filter: "Filter",
      note: "Overhang permissions apply to this store's shelf across future runs. Zero disables overhang.",
      help: "Physical shelf permission, in the same unit as shelf depth. Zero disables it. Products may overhang by at most 20% of their depth in the chosen orientation, one pack deep.",
      invalidLength: "Enter zero or a positive length with at most two decimal places.",
      ineligible: "Front overhang requires a level regular shelf with positive depth.",
      notConfirmed: "The server did not confirm this shelf permission. Reload and try again.",
      notSaved: "Overhang permission was not saved. Try again.",
    },
  },
  // shelfLoaderWhy, {{depth}} in the merchant's unit.
  shelfLoader: {
    why: {
      needed: "{{needing}} of {{facings}} facings need a spring loader",
      neededTypicalDepth:
        "{{needing}} of {{facings}} facings need a spring loader: {{depth}}, like the base planogram's loader shelves",
      needsMoreThanHalf:
        "{{needing}} of {{facings}} facings need a spring loader (needs more than half)",
      neededWithoutLoader:
        "{{needing}} of {{facings}} facings need a spring loader; this shelf has no loader",
      notNeeded: "No product here needs a spring loader",
      loaderWithoutNeed: "No product here needs a spring loader; the shelf has one",
      setNoneOnFixturesPage:
        "Set to none on the Fixtures page for this store. {{needing}} of {{facings}} facings need one.",
      setOnFixturesPage: "Set to {{depth}} on the Fixtures page for this store.",
      depthNotSet: "{{needing}} of {{facings}} facings need a spring loader; depth not set",
      onBaseShelf:
        "Stays with the shelf: the base planogram's shelf has a {{depth}} spring loader. {{needing}} of {{facings}} facings need one.",
      notOnBaseShelf:
        "Stays with the shelf: the base planogram's shelf has no spring loader. {{needing}} of {{facings}} facings need one.",
    },
    // The shelf panel's spring loader rows; a PSA stores the loader as a negative back overhang.
    rows: {
      label: "Spring loader",
      depth: "{{depth}} (back overhang -{{depth}})",
      depthOnFixturesPage: "{{depth}} (back overhang -{{depth}}), set on the Fixtures page",
      usableDepth: "Usable depth",
      none: "None",
      noneOnFixturesPage: "None, set on the Fixtures page",
    },
    // The Fixtures page's spring loader depth column.
    fixtures: {
      header: "Spring loader depth",
      headerWithUnit: "Spring loader depth ({{unit}})",
      tooltip:
        "Depth the spring loader takes behind the products on this shelf. Blank: follow the spring loader rule. 0: no loader.",
      backOverhang: "Stored in a PSA as back overhang -{{depth}}",
      notSet: "Not set",
      none: "None",
      notSaved: "Not saved. Try again.",
    },
  },
  shelfWeight: {
    label: "Weight",
    load: "{{pounds}} lb",
    // Provenance: a stated weight and a guessed one must not read alike. The
    // note names the weakest evidence in the total — see shelfWeightLabel.ts.
    loadWithNotes: "{{pounds}} lb ({{notes}})",
    measured: "measured",
    allDerived: "derived",
    someDerived: "{{count}} derived",
    unweighed: "{{count}} unweighed",
    bulk: "BULK",
    overloadedLabel: "Overloaded",
    overloadedRequestDefault: "over the {{pounds}} lb limit set for this run",
    overloadedFixture: "over the fixture's {{pounds}} lb limit",
    limitLabel: "Weight limit",
    noLimit: "no weight limit set for this run",
    pogTotal: "Total Weight",
  },
  widthFormatted: "{{value}} m {{cm}} cm",
  preparedBy: "Prepared By",
  approvedBy: "Approved By",
  categoryOwner: "Category Owner",
  dateEffective: "Date Effective",
  segment: "Segment",
  page: "Page",
  newItemSummary: "New Item Summary",
  deletedItemSummary: "Deleted Item Summary",
  shelfSemanticReport: "Shelf Semantic Report",
  width: "Width",
  depth: "Depth",
  height: "Height",
  highlights: {
    defaultInsertedItemName: "New Inserted Item",
    newItemBadge: "PI",
    itemWeight: "Item Weight",
    itemWeightSublabel: "lb on shelf",
    shelfWeight: "Shelf Weight",
    shelfWeightSublabel: "lb on board",
    shelfWeightPct: "Shelf Weight %",
    shelfWeightPctSublabel: "of limit",
    overloaded: "Overloaded",
    overloadedSublabel: "over limit",
  },
  planogramCanvas: {
    rendererToggle: "Canvas renderer",
    showDividersToggle: "Show dividers",
    itemCount: "{{count}} items",
    invalidProductDimensions: "This product cannot be added because its dimensions are invalid.",
    moveFeedback: {
      alreadyAtPosition: "That product is already at that position.",
      missingTarget: "Drop onto a product or the space above a shelf.",
      incompatibleTarget: "This product cannot be placed on that fixture.",
      priorityOrder: "That move would break the product order. Choose another position.",
      collision: "There is not enough clear space at that position.",
      fallback: "This move could not be made. Try another product or shelf.",
    },
    newItemIndicator: "New item indicator",
    aisleBreak: {
      breakHere: "Break the aisle here",
      breakBetween: "Break the aisle between bays {{leftBayNo}} and {{rightBayNo}}",
      joinBays: "Join bays {{leftBayNo}} and {{rightBayNo}}",
      setBy: "Aisle break. Set by {{name}} on {{date}}.",
      setInEditor: "Aisle break. Set in the editor.",
      fromFixtureFile: "Aisle break. From the fixture file.",
    },
    itemMenu: {
      delete: "Delete",
      duplicate: "Duplicate",
      flipHorizontally: "Flip Horizontally",
      sideView: "Side View",
      unrange: "Unrange",
    },
    bayMenu: {
      addBayLeft: "Add Bay Left",
      addBayRight: "Add Bay Right",
      deleteBay: "Delete Bay",
      newShelf: "New Shelf",
      title: "Bay {{bayNo}}",
      baysKeptByAisleBreak:
        "This run has an aisle break, so its bays change only on the store's fixtures.",
    },
    containerMenu: {
      delete: "Delete",
      splitBottom: "Split to Bottom",
      splitRight: "Split to Right",
      unrange: "Unrange",
    },
    shelfMenu: {
      delete: "Delete",
      duplicate: "Duplicate",
      flipHorizontally: "Flip Horizontally",
      mergeLeft: "Merge left",
      mergeRight: "Merge right",
      moveDown: "Move down",
      moveUp: "Move up",
      sortCdt1: "Sort by CDT1 Priority",
      sortCdt2: "Sort by CDT2 Priority",
      sortCdt3: "Sort by CDT3 Priority",
      unmergeLeft: "Unmerge left",
      unmergeRight: "Unmerge right",
      viewUnranged: "View Unranged",
    },
  },
  metricFilter: {
    label: "Show stores scoring below",
    anyScore: "Any score",
    thresholdTitle: "Score",
    thresholdPlaceholder: "0-100",
    apply: "Apply",
    clear: "Clear",
    notScored: "Not scored",
    exportAllUnavailable: "Clear the score filter to export across all stores.",
  },
  planogramMetrics: {
    returnOnSpace: {
      title: "Return on Space",
      description:
        "Whether each part of the category gets shelf space in line with what it sells. A high score means nothing is starved — giving a group more room than it earns is not marked down.",
    },
    coreRange: {
      title: "Core Range",
      description:
        "How many of your must-stock products actually made it onto the shelf. Extra facings of the same product count once.",
      emptyReason:
        "No products on this plan are flagged as core range, so there is nothing to measure against.",
    },
    shelfAlignment: {
      title: "Shelf Alignment",
      description:
        "Whether shelves line up at the same height as you look along the fixture, bay to bay. Shelves that are close but not level count as a miss.",
    },
    dosMos: {
      title: "DOS / MOS",
      description:
        "How many products hold enough stock on shelf to keep trading between deliveries. A product counts against the score if it misses either its days-of-supply or its units-on-shelf target.",
    },
    aesthetics: {
      title: "Aesthetics",
      description:
        "How tidy and shoppable the plan looks: clean blocks, right flow, full shelves, heavy and big packs low. Read it alongside sales.",
      breakdown: "Score breakdown",
      reasonsTitle: "Where points are lost",
      gatesTitle: "Failed checks",
      notGraded: "n/a",
      blockingParts: {
        shape: "block shape",
        edges: "straight edges",
        columns: "vertical blocking",
        bands: "horizontal blocking",
      },
      gradedAgainstRelease: "Score {{version}}, graded against CDT release {{release}}",
      gradedAgainstColumns: "Score {{version}}, graded against the plan's own vertical blocking",
      gradedAgainstOwnHorizontal:
        "Score {{version}}, graded against the plan's own horizontal blocking",
      releaseUnavailable:
        "Not graded: the CDT release this planogram was generated from could not be loaded. Reload the page to try again.",
      requestUnavailable:
        "Not graded: the request this planogram was generated from could not be loaded. Reload the page to try again.",
      bandSource: {
        draft: "Shelf rules: the draft's accepted bands",
        request: "Shelf rules: the restrictions this plan's generation was sent",
        emission: "Shelf rules: the CDT rules emitted for this store's generation",
        legacyFences:
          "Shelf rules: legacy fences stored on the plan, not the generation's restrictions",
      },
      coreRangeSource: {
        request: "Must-place products: the core range this plan's generation was sent",
        pogFlag: "Must-place products: the products the plan flags as core range",
      },
      points: "−{{points}}",
      parts: {
        blocking: "Blocking",
        cdt: "Flow",
        fullness: "Shelf fill",
        rulesKept: "Placement rules",
        ergonomics: "Heavy & big low",
        range: "Range",
      },
      reasons: {
        blockingPair: "{{first}} and {{second}} blocks are broken",
        blockingOne: "{{first}} block is broken",
        blockingPlain: "Some blocks are broken",
        ruleFence: "{{product}} not allowed on shelf {{shelf}}",
        ruleMissing: "{{band}} missing from shelf {{shelf}}",
        rulePlain: "Placement rules not followed",
        gap: "Gap of {{gap}} {{unit}} on shelf {{shelf}}",
        fullnessPlain: "Shelves not full",
        brandDropped: "{{brand}} dropped",
        unplaced: "{{count}} products not placed",
        sizeOrder: "{{block}} sizes out of order",
        orderPlain: "{{block}} out of flow",
        cdtPlain: "Flow not followed",
        heavyHigh: "Heavy packs too high",
        bigOverSmall: "Big packs above small",
      },
      gates: {
        overlap: "Products overlap",
        "past-edge": "Products overhang the shelf",
        "empty-shelf": "Empty shelf",
        unplaced: "Products not placed",
        "brand-eliminated": "Brand dropped",
        "block-in-pieces": "Brand split into 2+ blocks",
        "block-staircased": "A top-level column has uneven edges",
        "block-out-of-shape": "Brand block doesn't fill its rectangle",
        "band-broken": "Product on a shelf it's not allowed on",
        "shelves-not-full": "Gaps on shelves",
      },
    },
    expectedSales: {
      title: "Expected Sales",
      description:
        "What everything on this plan is expected to sell, added up — counted once per product, whatever its facings. Read it against the scores to its left: a tidier plan that drops products looks better there and worse here.",
      emptyReason:
        "This plan has no real sales figures against it — every product carries the same placeholder, so a total would be a made-up number. Re-run the data load to bring sales in.",
    },
  },
  planogramProductDetails: {
    bulkWeight: "{{weight}} lb · BULK",
    weight: "Weight",
    weightValue: "{{weight}} lb",
    itemId: "Item ID",
    notAvailable: "Not available",
    stockCoverUnavailable: "Stock cover is not available for this fixture.",
    capacity: "Capacity",
    dos: "DOS",
    mos: "MOS",
    dosTitle: "Days of supply, totaled across this product's displays in the plan shown.",
    mosTitle: "Minimum-on-shelf compliance. 100% meets the minimum.",
    daysUnit: " days",
    sales: "Sales",
    ranking: "Ranking",
    profit: "Profit",
  },
  planogramHover: {
    detailsLabel: "Planogram hover details",
    source: {
      before: "Before",
      after: "After",
      saved: "Saved plan",
    },
    inspectionPaused: "Blink paused while inspecting · move off the product to resume.",
  },
  pegboardFacingChange: {
    indicator: "Facing update: {{changeType}}",
    inserted: "Inserted",
    swappedIn: "Swapped in",
    facingsIncreased: "Facings increased",
    facingsDecreased: "Facings decreased",
    swappedOut: "Swapped out",
    deleted: "Deleted",
  },
  editShelfModal: {
    menuLabel: "Edit shelf",
    multipleShelvesEditUnavailable: "Edit shelf is only available when one shelf is selected.",
    title: "Edit shelf",
    widthLabel: "Shelf width (inches)",
    widthPlaceholder: "Enter shelf width",
    mergedWidthDisabled: "Width cannot be changed for merged shelves.",
    merchandisableWidthLabel: "Merchandisable width (inches)",
    merchandisableWidthPlaceholder: "Enter merchandisable width",
    mergedMerchandisableWidthDisabled: "Merchandisable width cannot be changed for merged shelves.",
    obstructionMerchandisableWidthDisabled:
      "An obstruction blocks part of this shelf, so its merchandisable width comes from the planogram.",
    merchandisableSpaceLabel: "Merchandisable space",
    invalidWidth: "Please enter a valid positive number for shelf width.",
    invalidMerchandisableWidth: "Please enter a valid merchandisable width within the shelf width.",
    alignLabel: "Shelf alignment",
    alignLeft: "Left",
    alignRight: "Right",
    alignBetween: "Between",
    alignAround: "Around",
    changeShelfTypeTitle: "Change shelf type to {{shelfType}}?",
    incompatibleProductDescriptionOneShelf:
      "1 product on this shelf can't be merchandised on a {{shelfType}} fixture ({{names}}). Saving will move it to the unranged list.",
    incompatibleProductDescriptionMultipleShelves:
      "1 product on these shelves can't be merchandised on a {{shelfType}} fixture ({{names}}). Saving will move it to the unranged list.",
    incompatibleProductsDescriptionOneShelf:
      "{{count}} products on this shelf can't be merchandised on a {{shelfType}} fixture ({{names}}). Saving will move them to the unranged list.",
    incompatibleProductsDescriptionMultipleShelves:
      "{{count}} products on these shelves can't be merchandised on a {{shelfType}} fixture ({{names}}). Saving will move them to the unranged list.",
    incompatibleProductsAndMore: "and {{count}} more",
    changeAndUnrange: "Change & Unrange",
  },
  cdtTreeEditor: {
    button: "CDT Tree",
    accessDenied: "You do not have permission to view decision trees.",
    loadingCategory: "Loading Category CDT Tree...",
    categoryLoadFailed: "Failed to load Category CDT Tree",
    invalidCategory: "The category ID is invalid.",
    invalidSelection: "The version or category selection is invalid.",
    loadingTree: "Loading version...",
    treeLoadFailed: "Failed to load version.",
    refreshFailed: "Couldn't refresh this version. You're editing the last loaded copy.",
    refreshRetry: "Retry",
    saveSuccess: "CDT Hierarchy saved successfully!",
    saveFailed: "Failed to save CDT hierarchy",
    saveUnavailable: "This local CDT draft cannot be saved to the API.",
    saveNewUnavailable: "Saving a new CDT hierarchy is not available yet.",
    saveNewSuccess: "Created {{version}}. Your changes are saved on the new version.",
    saveNewUnsaved:
      "Created {{version}}, but your changes could not be saved on it yet. Save again to retry.",
    nextUnavailable: "Next navigation is not available yet.",
    resetSuccess: "CDT hierarchy reset to ETL data.",
    resetFailed: "Failed to reset CDT hierarchy.",
    resetRefreshFailed:
      "Reset succeeded, but the refreshed hierarchy could not be loaded. Reload the page to continue.",
    unassigned: "(Unassigned)",
    versions: {
      title: "Decision Trees",
      versionsTab: "Versions",
      storesTab: "Stores",
      category: "Category {{categoryId}}",
      searchVersions: "Search versions",
      searchStores: "Search stores",
      versionCount: "{{visible}} of {{total}} versions",
      storeCountSummary:
        "{{visible}} of {{total}} stores · {{pinned}} pinned · {{onPrimary}} on primary",
      id: "ID",
      name: "Name",
      source: "Source",
      primaryColumn: "Primary CDT",
      storesColumn: "Stores",
      updated: "Updated",
      open: "Open",
      user: "User",
      etlReadOnly: "ETL · Read-only",
      setPrimary: "Set primary",
      primaryStar: "★ Primary CDT",
      primary: "Primary",
      pinned: "Pinned",
      storeCount: "{{count}} {{storeLabel}}",
      storeCountWithPrimary: "{{count}} {{storeLabel}} ({{onPrimaryCount}} on primary)",
      store: "store",
      stores: "stores",
      pinnedStore: "pinned store",
      pinnedStores: "pinned stores",
      moveVerbSingular: "moves",
      moveVerbPlural: "move",
      keepVerbSingular: "keeps theirs",
      keepVerbPlural: "keep theirs",
      revisionDate: "{{date}}",
      storeCode: "Store code",
      storeName: "Store name",
      versionUsed: "Version used",
      storeUses: "Uses",
      cluster: "Cluster",
      allClusters: "All clusters",
      region: "Region",
      bays: "Bays",
      versionReference: "#{{id}} {{name}}",
      unnamedStore: "Unnamed store",
      unknownVersion: "Unknown version",
      loadingVersions: "Loading versions...",
      versionsLoadFailed: "Failed to load versions.",
      loadingStores: "Loading stores...",
      storesLoadFailed: "Failed to load stores.",
      noVersions: "No versions exist for this category.",
      noMatchingVersions: "No versions match your search.",
      noStores: "No stores use this category.",
      noMatchingStores: "No stores match your search.",
      retry: "Retry",
      confirmPrimaryTitle: "Set this version as the primary CDT?",
      confirmPrimaryDescription:
        "{{onPrimaryCount}} {{onPrimaryStoreLabel}} on primary {{moveVerb}} from {{oldVersion}} to {{nextVersion}}; {{pinnedCount}} {{pinnedStoreLabel}} {{keepVerb}}.",
      cancel: "Cancel",
      primaryUpdated: "{{version}} is now the primary CDT.",
      primaryUpdateFailed: "Could not set the primary CDT.",
      newCdt: "New CDT",
      cloneCreated: "Created {{version}}.",
    },
  },
  cdtClone: {
    titleNewCdt: "New CDT",
    titleSaveNew: "Save as new version",
    close: "Close",
    sourceLabel: "Copy from",
    sourcePlaceholder: "Select a version",
    sourceMissing: "No version to copy from",
    sourceEtl: "ETL",
    sourceUser: "User",
    sourcePrimary: "Primary",
    sourceStoreCount: "{{count}} stores",
    sourceStoreCount_one: "{{count}} store",
    nameLabel: "Name",
    namePlaceholder: "Name of the new version",
    defaultName: "{{name}} (copy)",
    duplicateName: "A version with this name already exists",
    nameTooLong: "Use {{max}} characters or fewer (now {{count}}).",
    failed: "Could not create the new version. Try again.",
    note: "The new version is editable, is not the primary CDT, and has no pinned stores.",
    cancel: "Cancel",
    confirmNewCdt: "Create",
    confirmSaveNew: "Save new version",
    creating: "Creating…",
  },
  cdtWarnings: {
    nodeNameBlank: "Node name cannot be blank.",
    subgroupNameBlank: "Subgroup name cannot be blank.",
    cannotAddBelow: "A subgroup cannot be added below CDT3.",
    saveParentFirst: "Save the new parent before adding another subgroup.",
    cdt0CannotReparent: "CDT0 nodes cannot be reparented.",
    cannotReparentIntoSubtree: "A node cannot be reparented into its own subtree.",
    nodeNotFound: "Node {{node}} was not found.",
    parentNotFound: "Parent node {{node}} was not found.",
    invalidReorder: "The reorder references an invalid node.",
    reorderSiblings: "Reordering is only valid between siblings under the same parent.",
    invalidMerge: "The merge references an invalid node.",
    invalidNodeParent: "The node or parent reference is invalid.",
    targetParentLevel: "The target parent must be {{expected}}, but is {{actual}}.",
    renameCollision: "A rename collision was detected with node {{node}}.",
  },
  cdtTable: {
    hierarchyNode: "Hierarchy Node",
    productCount: "Product Count",
    alerts: "Alerts",
    reorder: "Reorder",
    products: "products",
    clean: "Clean",
    prioritySequence: "Priority Sequence",
    priorityDescription: "Derived minimum descendant product priority",
    zoomIn: "Zoom in",
    zoomOut: "Zoom out",
    fitView: "Fit to view",
    productCountValue: "{{count}} products",
  },
  cdtHeader: {
    tree: "Tree",
    list: "List",
    priority: "CDT Priority",
    undo: "Undo",
    redo: "Redo",
    save: "Save",
    togglePriority: "Toggle priority badges",
    expandAll: "Expand all",
    collapseAll: "Collapse all",
    resolveAlerts: "Resolve alerts",
    category: "CDT 0: Category",
    categoryName: "Category",
    segmentName: "Segment",
    brandName: "Brand",
    flavourName: "Flavour",
    revertChanges: "Revert changes",
    saveNew: "Save new",
    saveNewVersion: "Save new version",
    next: "Next",
    breadcrumb: "Breadcrumb",
    breadcrumbCategories: "Categories",
    breadcrumbDecisionTrees: "Decision Trees",
    chipEtl: "ETL",
    chipPrimary: "Primary CDT",
    sourceEtl: "ETL snapshot",
    sourceEtlDated: "ETL snapshot · {{date}}",
    sourceUser: "User version",
    updated: "Updated {{date}}",
    readOnlyBanner:
      "ETL CDT — read only. Changes are saved as a new version; this one stays as loaded.",
  },
  cdtStores: {
    title: "Stores on {{version}}",
    close: "Close",
    closeButton: "Close",
    intro:
      "Tick the stores that should use this version. Unticked stores return to the primary CDT.",
    introPrimary:
      "Stores on the primary already use this version. Ticking one pins it here so a later primary change does not move it.",
    introReadOnly:
      "You can see which stores use this version. Pinning needs the CDT edit permission.",
    searchPlaceholder: "Search by store code or name",
    cluster: "Cluster",
    allClusters: "All clusters",
    region: "Region",
    allRegions: "All regions",
    bays: "Bays",
    allBays: "All bays",
    selectVisible: "Select all visible",
    deselectVisible: "Deselect all visible",
    counter: "{{visible}} of {{total}} stores · {{selected}} selected",
    storeCode: "Store code",
    storeName: "Store name",
    versionUsed: "Version used",
    change: "Change",
    unnamedStore: "Unnamed store",
    unknownVersion: "Unknown version",
    primary: "Primary",
    pinned: "Pinned",
    hintPinnedHere: "pinned #{{id}}",
    hintPinned: "pinned #{{id}}",
    hintPrimary: "primary",
    hintMovesFrom: "moves from #{{id}}",
    hintMovesFromPrimary: "moves from primary",
    hintReturnsToPrimary: "returns to primary",
    noMatchingStores: "No stores match the filters.",
    progress: "Applying {{done}} of {{total}}…",
    failuresTitle: "{{count}} change(s) did not apply. The others are saved.",
    failurePin: "Could not pin store {{store}}",
    failureUnpin: "Could not return store {{store}} to the primary",
    retryFailed: "Retry {{count}} failed",
    cancel: "Cancel",
    done: "Done ({{pin}} to pin, {{unpin}} to release)",
    applying: "Applying…",
    summary: "{{pinned}} {{pinnedLabel}}, {{unpinned}} returned to primary",
    pinnedOne: "pinned",
    pinnedMany: "pinned",
    openStores: "Stores",
    storesChip: "{{count}} {{storeLabel}}",
  },
  cdtNext: {
    title: "Next step for {{version}}",
    close: "Close",
    storesUsing: "{{count}} stores use this version and will be preselected.",
    storesUsing_one: "{{count}} store uses this version and will be preselected.",
    generateTitle: "Generate new merchflow",
    generateText:
      "Start a UHD flow for this category with the stores that use this version preselected. The planogram rules open with Primary / pinned CDT selected.",
    updateTitle: "Update existing planograms",
    updateText:
      "Pick a flow of this category that already has store planograms and run a Flow Update with Primary / pinned CDT selected.",
    flowLabel: "Flow",
    flowPlaceholder: "Select a flow",
    flowsLoading: "Loading flows…",
    noFlows: "No flow of this category has store planograms yet.",
    flowsLoadFailed: "Could not load the flows of this category.",
    categoryLoadFailed: "Could not load this category's details, so the next step can't start yet.",
    retry: "Retry",
    cancel: "Cancel",
    generateAction: "Generate new merchflow",
    updateAction: "Open Flow Update",
    nextDisabledDirty: "Save your changes before continuing.",
    next: "Next →",
    flowFromVersion:
      "Started from CDT version {{version}}. {{count}} {{storeLabel}} use it and are preselected on the Stores step; the planogram rules will default to Primary / pinned CDT.",
  },
  cdtFlowReleases: {
    title: "Flow releases",
    tooltip: "Flow releases apply to stores in that flow that have no pin.",
    tree: "Tree",
    flow: "Flow",
    basePog: "Base planogram",
    release: "Release",
    finalized: "Finalized",
    openBasePog: "Base POG #{{id}}",
    saveAsVersion: "Save as category version",
    defaultVersionName: "{{flow}} release",
  },
  cdtCategorySummary: {
    loading: "Loading…",
    summary: "{{versions}} {{versionLabel}} · {{pinned}} {{pinnedLabel}} pinned",
    version: "version",
    versions: "versions",
    store: "store",
    stores: "stores",
  },
  storePogsCdtVersion: {
    header: "CDT version",
    pinned: "pinned",
    primary: "primary",
    unknown: "—",
  },
  cdtSaveConfirm: {
    title: "Save changes to this version?",
    description:
      "{{count}} {{storeLabel}} {{useVerb}} this version ({{pinnedCount}} pinned, {{primaryCount}} via primary). Their next generation or update picks up the new sequence.",
    store: "store",
    stores: "stores",
    useVerbSingular: "uses",
    useVerbPlural: "use",
    noStores: "No stores use this version yet.",
    usageLoading: "Checking which stores use this version…",
    usageFailed: "Couldn't check which stores use this version. Retry before saving.",
    retry: "Retry",
    close: "Close save confirmation",
    cancel: "Cancel",
    confirm: "Save",
  },
  cdtRename: {
    title: "Rename Group Collision",
    existing: 'A sibling group named "{{name}}" already exists!',
    mergeDescription: "Renaming will merge these two groups and combine their descendant subtrees.",
    cancel: "Cancel",
    close: "Close rename collision dialog",
    confirm: "Confirm Merge",
  },
  cdtDelete: {
    title: "Delete Subtree Confirmation",
    question: 'Are you sure you want to delete group "{{name}}"?',
    descendants: "Descendant CDT nodes to be removed:",
    close: "Close delete confirmation",
    cancel: "Cancel",
    delete: "Delete Subtree",
  },
  cdtReset: {
    title: "Reset to ETL data",
    warning: "This discards saved customization and unsaved hierarchy edits.",
    close: "Close reset confirmation",
    cancel: "Cancel",
    confirm: "Reset to ETL",
    resetting: "Resetting…",
  },
  cdtCanvas: {
    categoryRoot: "Category",
    selectNode: "Select {{name}}",
    expandNode: "Expand {{name}}",
    collapseNode: "Collapse {{name}}",
    moveUp: "Move up",
    moveDown: "Move down",
  },
  cdtNodeInspector: {
    title: "Node Inspector",
    group: "Group",
    categoryRoot: "Category Root",
    nodeName: "Node Name",
    aggregateItemCount: "Items in hierarchy, including descendants ({{count}})",
    immediateChildren: "Immediate children",
    noImmediateChildren: "No immediate children.",
    viewProducts: "View products",
    saveBeforeViewingProducts: "Save the hierarchy before viewing products.",
    addSubgroup: "Add {{level}} Subgroup",
    newGroupName: "New {{level}} group name",
    addSubgroupAction: "Add Subgroup",
    deleteGroup: "Delete this group",
    activeAlerts: "Active Alerts ({{count}})",
    noActiveAlerts: "No active alerts for this node.",
    suggestionsComingSoon: "Suggestions (Coming soon)",
    comingSoon: "Coming soon",
    selectNode: "Select a node on the canvas to inspect details.",
    submitRename: "Submit rename",
    saveParentFirst: "Save the new parent before adding another subgroup",
  },
  cdtProducts: {
    regionLabel: "Products in {{name}}",
    title: "Products in {{name}}",
    directCountOne: "1 direct product",
    directCount: "{{count}} direct products",
    searchPlaceholder: "Search products",
    close: "Close products drawer",
    loading: "Loading products…",
    loadFailed: "Products could not be loaded.",
    retry: "Retry",
    noProducts: "No products are assigned directly to this node.",
    noSearchResults: "No products match this search.",
    pageSize: "Rows per page",
    product: "Product",
    segment: "Segment",
    subcategory: "Subcategory",
    brand: "Brand",
    sku: "SKU",
    price: "Price",
    position: "Position",
    notAvailable: "Not available",
  },
  cdtAlerts: {
    renamePlaceholder: "New name...",
    rename: "Rename",
    groupScope: "Group: {{name}}",
    draftScope: "Draft-level alerts",
    affectedGroup: "Affected group: {{name}}",
    affectedDraft: "Affected scope: current draft",
    similarGroup: 'Similar CDT group name "{{name}}" under the same parent',
    guidedTitle: "Guided Alert Resolution",
    close: "Close alert resolution",
    similarGroups: "Similar Groups:",
    affectedScope: "Affected scope",
    affectedAlertScope: "Affected alert scope",
    allActive: "All active alerts",
    showing: "Showing {{shown}} of {{total}} active alerts",
    noAlerts: "No active CDT hierarchy alerts!",
    noScopedAlerts: "No active alerts in this affected scope.",
    focusGroup: "Focus group",
    editRequired: "Edit mode required for repair",
  },
  cdtStudioTab: {
    tabLabel: "CDT Studio",
    configurationLabel: "CDT Studio configuration",
    unavailable: {
      notPlanogram: "CDT Studio is available only in a Planogram Editor.",
      missingMerchflow: "Open this planogram from a merchflow to use CDT Studio.",
      missingPermission: "Your role needs CDT edit permission to use CDT Studio.",
      categoryUnavailable:
        "CDT Studio needs exactly one category in the current planogram context.",
      configurationRequired:
        "No CDT Studio configuration is available for this category. Open Categories and save the configuration for this category.",
      unsupportedFixtures:
        "CDT Studio can't read this planogram: a pegboard product hangs from an invalid hole. Fix its hole position to use CDT Studio.",
    },
  },
  planogramHighlights: {
    pinLabelToTop: "Pin {{label}} to top",
    unpinLabel: "Unpin {{label}}",
  },
  cdtPriorityOrder: {
    tabLabel: "Re-order CDTs",
    cdtLevel: "CDT Level",
    cdt0: "CDT0",
    cdt1: "CDT1",
    cdt2: "CDT2",
    cdt3: "CDT3",
    searchPlaceholder: "Search CDT or product",
    expandGroup: "Expand {{groupName}}",
    collapseGroup: "Collapse {{groupName}}",
    cdt0NoPriority: "CDT0 uses variant and has no stored priority field",
    productCount: "{{count}} product",
    productCountPlural: "{{count}} products",
    unplaced: "Unplaced",
    includesUnplaced: "Includes unplaced",
    ranged: "Ranged",
    unranged: "Unranged",
    placementLabel: "Bay {{bayNo}}, Shelf {{shelfNo}}",
    pegboardPlacementLabel: "Pegboard {{pegboardId}}, hole {{holeNoLeft}}, {{holeNoTop}}",
    mixed: "Mixed",
    noCdt: "(No CDT)",
    noData: "No CDT priority data",
    mode: "Mode",
    imageMode: "Image",
    labelMode: "Label",
    productCanvas: "CDT products",
    selectProduct: "Select {{productName}}",
    moveProductLeft: "Move {{productName}} left",
    moveProductRight: "Move {{productName}} right",
    zoom: "Zoom",
    zoomIn: "Zoom in",
    zoomOut: "Zoom out",
    resetZoom: "Reset zoom",
    viewportControls: "Planogram viewport controls",
    resetViewport: "Reset viewport",
    reset: "Reset",
    planogramContainers: "Planogram containers",
    planogramProducts: "Planogram products",
    planogramPegboards: "Planogram pegboards",
    planogramPlacements: "Planogram placements",
    planogramShelves: "Planogram shelves",
    planogramFixtures: "Planogram fixtures",
    selectContainer: "Select {{label}}",
    selectPegboard: "Select pegboard {{id}}",
    selectPlacement: "Select {{label}}",
    selectShelf: "Select shelf {{shelfNo}}",
    selectFixture: "Select {{fixtureName}} {{shelfNo}}",
    shelf: "Shelf {{shelfNo}}",
    fixture: "{{fixtureName}} {{shelfNo}}",
    riserFixture: "riser",
    hangcellFixture: "hangcell",
    moveLeft: "Move left",
    moveRight: "Move right",
    resizeShelfWider: "Resize shelf {{shelfNo}} wider",
    resizeShelfNarrower: "Resize shelf {{shelfNo}} narrower",
    resizeShelfWiderText: "Resize shelf wider",
    resizeShelfNarrowerText: "Resize shelf narrower",
    resizeFixtureNarrower: "Resize {{fixtureName}} {{shelfNo}} narrower",
    resizeFixtureNarrowerText: "Resize fixture narrower",
    resizeFixtureWider: "Resize {{fixtureName}} {{shelfNo}} wider",
    resizeFixtureWiderText: "Resize fixture wider",
    invalidPegboardHole: "Choose an available pegboard hole.",
    shelfCollision: "Move the shelf to an open position.",
    shelfResizeCollision: "Choose a fixture size that does not overlap another fixture.",
    shelfMoveFailed: "The shelf could not be moved. Try another position.",
    shelfResizeFailed: "The fixture could not be resized. Try another size.",
    pegboardMoveFailed: "The product could not be moved on the pegboard. Try another hole.",
    productMoveRejected: "The product could not be moved. Try another position.",
    planogramMoveFailed: "The planogram change could not be completed. Try again.",
    labelOptions: {
      none: "None",
      brand: "Brand",
      categoryCode: "Category code",
      cdt0: "CDT0",
      cdt1: "CDT1",
      cdt1Priority: "CDT1 priority",
      cdt2: "CDT2",
      cdt2Priority: "CDT2 priority",
      cdt3: "CDT3",
      cdt3Priority: "CDT3 priority",
      color: "Color",
      facings: "Facings",
      name: "Name",
      price: "Price",
      productCode: "Product code",
      quantity: "Quantity",
      ranking: "Ranking",
      salesAmount: "Sales amount",
      size: "Size",
      subCategory: "Subcategory",
      upc: "UPC",
      variant: "Variant",
    },
    confirmSnakingAriaLabel: "Confirm snaking pattern",
    snakingPatternAriaLabel: "Snaking pattern configuration",
    confirmSnakingTitle: "Confirm snaking pattern",
    confirmSnakingDescription:
      "Review the inferred row directions and bay row order before enabling CDT editing.",
    snakingPatternTitle: "Snaking pattern configuration",
    snakingPatternDescription: "Review the configured row directions and bay row order.",
    aisleBreak: "Aisle break",
    bayLabel: "Bay {{bayNo}}",
    shelfLabel: "Shelf {{shelfName}}",
    bayShelfOrderAriaLabel: "Bay {{bayNo}} shelf order",
    shelfDirectionAriaLabel: "Shelf {{shelfName}} direction",
    pegboardRowLabel: "Pegboard {{pegboardId}}, row {{rowNo}}",
    pegboardRowDirectionAriaLabel: "Pegboard {{pegboardId}}, row {{rowNo}} direction",
    selectShelfOrder: "Select shelf order",
    topFirst: "Top-first",
    bottomFirst: "Bottom-first",
    selectDirection: "Select direction",
    leftToRight: "Left-to-right",
    rightToLeft: "Right-to-left",
    incompleteSnakingConfiguration:
      "Complete every bay and traversal-row direction. All segments of a merged shelf must use the same row direction.",
    confirm: "Confirm",
    snakingUnavailableTitle: "CDT Planogram Editor unavailable",
    snakingUnavailableMessage:
      "REGULAR shelves and pegboards are supported. RISER, HANGCELL, and other unsupported fixtures block the entire CDT Planogram Editor.",
    confirmSnakingBeforeDrag:
      "Confirm the Snaking Pattern configuration before using CDT drag and drop.",
    priorityOrderViolation:
      "Priority Order Violation: Traversal order does not match ascending stored CDT3 priority. Drag and drop is disabled.",
    duplicateCdtPath:
      "Duplicate CDT Path: CDT movement and priority recalculation are disabled until the duplicate is fixed.",
    duplicateCdtPathDetails: "{{path}} — products: {{productCodes}}",
    recalculationFailed:
      "Full Priority Recalculation could not be completed. The planogram is unchanged.",
    productMoveFailed:
      "The product could not be moved because CDT priorities could not be recalculated. The planogram is unchanged.",
    recalculatePriorities: "Full Priority Recalculation",
    viewSnakingPattern: "View Snaking Pattern",
    hideSnakingPattern: "Hide Snaking Pattern",
  },
  unsavedChanges: {
    title: "Unsaved Changes",
    description:
      "You have unsaved changes on this planogram. Save them before leaving, or leave anyway and lose them.",
    keepEditing: "Keep Editing",
    leaveAnyway: "Leave Anyway",
    save: "Save",
    saveFailed: "Failed to save planogram changes",
  },
  productsCdtsChanged: {
    title: "Products CDTs have been changed",
    description:
      "Products CDT changes can be saved only to the base planogram or also update products CDTs for all stores",
    saveOnly: "Save Only",
    saveAndUpdateProducts: "Save and Update Products",
  },
  planogram: {
    facingsAndPlacement: "Facings & Placement",
    assortment: "Assortment",
  },
  basePog: {
    unavailableTitle: "No base planogram",
    unavailableMessage: "This store was imported from PSA and has no base planogram to review.",
  },
  sidebar: {
    performance: "Performance",
    assortment: "Assortment",
    spacePlanning: "Space Planning",
    imagine: "Imagine",
    categories: "Categories",
    stores: "Stores",
    products: "Products",
    fixtures: "Fixtures",
    projects: "Projects",
    merchflows: "Merchflows",
    storeSummary: "Store Summary",
    users: "Users",
    audit: "Audit",
    profile: "Profile",
    settings: "Settings",
    logout: "Log Out",
  },
  storesPickList: {
    columnStore: "STORE",
    columnBaySize: "BAY SIZE",
    columnBusinessType: "BUSINESS TYPE",
    columnActivityType: "ACTIVITY TYPE",
    searchPlaceholder: "Search stores",
    selectedCount: "{{selected}} selected / {{total}} in filter",
    loadingStores: "Loading stores...",
    nextButton: "Next ({{count}} {{label}})",
    storeLabel: "store",
    storesLabel: "stores",
    columnCdtVersion: "CDT VERSION",
    cdtPinned: "pinned",
    cdtPrimary: "primary",
    cdtVersionsInFlow: "CDT versions in this flow: {{list}}",
    cdtVersionsNone: "Select stores to see which CDT versions this flow will use.",
  },
  configCopilot: {
    name: "O",
    conciergePlanTitle: "How I will configure this run for you",
    conciergeAmbiguitiesTitle: "What I need you to clarify",
    conciergeExceptionsTitle: "Store-specific locations and fixtures",
    conciergeProposed: "Proposed · not yet applied",
    conciergeAccepted: "Accepted for this run",
    conciergeMeasured: "Measured evidence",
    conciergeCurrent: "Current policy · not newly confirmed",
    conciergeLimit: "Known limit",
    conciergeNeedsDecision: "Your decision needed",
    conciergeRecommendation: "O proposes",
    conciergeNoExceptions: "No store-specific decision is needed here yet.",
    conciergeNext: "We will review this after the current decision.",
    conciergeMoreEvidence: "{{count}} more measured details",
    conciergeReviewed: "{{count}} items reviewed",
    conciergeReady:
      "The decisions needed for this run are complete. Review the plan before generating.",
    conciergePending: "I still need your decision before this run can be generated.",
    conciergeHistory: "Conversation and changes",
    conciergePlanReviewed: "plan details",
    conciergeSupportingDetails: "Supporting details",
    conciergeAlsoProposed: "Also proposed · not yet applied",
    conciergeSectionDetails: "Review the recorded details",
    conciergeRetryReview: "Retry the store review",
    tagline: "sets your store rules",
    advanced: "Advanced settings",
    backToO: "Back to O",
    thinking: "Looking at your stores…",
    setup: "Rules for these stores",
    pick: "O's pick",
    alwaysOn: "Always on",
    fixFirst: "Fix before you run",
    worthKnowing: "Worth knowing",
    fix: "Fix",
    adjust: "Adjust",
    use: "Use",
    useAgain: "Use again",
    redo: "Redo",
    undone: "Undone.",
    redone: "Redone.",
    undoTitle: "Undo: {{title}}",
    redoTitle: "Redo: {{title}}",
    done: "Done",
    details: "Details",
    lastRunRunning: "{{done}} of {{stores}} stores done, still running",
    lastRunStores: "Last run, store by store",
    placeholder: 'Ask O, like "more products but keep brands together"',
    send: "Send",
    store: "Store",
    placed: "Placed",
    missing: "Not placed",
    lowShelves: "Shelves with gaps",
    bareShelves: "Empty shelves",
    fill: "Shelf space used",
    pastSupply: "Over max days of supply",
    score: "Tidiness score",
    failed: "Failed",
    running: "Running",
    generate: "Generate store planograms",
    cancel: "Cancel",
    retry: "Try again",
    brandBlocks: "Brand blocks",
    notUnderHorizontal: "Not under horizontal blocking",
    askO: "Ask O",
    offline: "I can't read typing right now; tap a suggestion or use Advanced settings.",
    notDone: "That didn't go through. Your rules are as you left them.",
    notSwitched: "Couldn't switch {{label}}. Your rules are as you left them.",
    notReread: "Change saved; the notes above may be out of date.",
    noDetails: "Couldn't load the run's details. Your rules are as you left them.",
    briefFailed: "O couldn't read your stores. Your rules are as you left them.",
    doneTitle: "Set for the next run: {{title}}.",
    alreadyFixed: "That's already fixed.",
    fixUnavailable: "I couldn't find a fix for that finding in this run.",
    changedSinceRun: "Changed since last run",
    changedFromSetup: "Changed from O's picks",
    backToLastRun: "Back to last run's rules",
    backToSetup: "Back to O's picks",
    backOnLastRun: "Back on the last run's rules: {{rows}}.",
    backOnSetup: "Back on O's picks: {{rows}}.",
    copiesBase: "Copying the base to every store, so these rules are ignored.",
    turnOff: "Turn off",
    favouriteFirst: "Only favourited base planograms reach Store Review.",
    onlyGenerate: "Only generate",
    favouriteAndGenerate: "Favourite and generate",
    verdictLost: "O lost track of this run. Reload the page to check on it.",
    readingRun: "Reading the run…",
    olderRunShown:
      "Showing the last finished run; a new run is going ({{done}} of {{stores}} done).",
    advancedChangedOne: "You changed 1 setting in Advanced: {{names}}.",
    advancedChangedMany: "You changed {{count}} settings in Advanced: {{names}}.",
    andMore: "{{names}} and {{count}} more",
    setByO: "Set by O: {{change}}",
    confirmedWithO: "Confirmed with O: {{change}}",
    leftOutUnprotected: "{{count}} not protected as core range",
    leftOutProtectedOf: "{{protected}} of {{products}} protected",
    leftOutWidthShort: "{{count}} didn't fit their brand block's space",
    leftOutSupply: "{{count}} couldn't reach min days of supply",
    leftOutBroken: "{{count}} would split a brand block or the sequence",
    leftOutBeforeSolve: "{{count}} removed before layout",
    studioPlanSkippedOne: "1 store ignored your CDT Studio plan",
    studioPlanSkippedMany: "{{count}} stores ignored your CDT Studio plan",
    qa: {
      good: "What's good?",
      bad: "What's bad?",
      better: "What can be better?",
      storesRun: "How did this run go?",
      storesResults: "What do these store results show?",
      storesAttention: "Which stores need attention?",
      storesNext: "What should I review first?",
      whyScore: "Why this score?",
      missing: "What's missing here?",
      cdtReady: "Is the CDT ready?",
      hello: "Ask me about this merchflow. I can explain what I find, but I won't change anything.",
      placeholder: "Ask about this merchflow",
      placeholderRunning: "Ask about the stores done so far",
      running: "Some stores are still generating.",
      thinking: "O is reading the merchflow…",
      unavailable: "I can't answer questions right now.",
      notAnswered: "That didn't go through. Ask again?",
      rateLimited: "O is busy. Try again in {{count}} seconds.",
      rateLimitReady: "O is ready to try again.",
      close: "Close",
      feedbackUp: "Helpful",
      feedbackDown: "Not helpful",
      feedbackWhat: "What was wrong or missing?",
      feedbackSend: "Send",
      feedbackThanks: "Thanks",
      feedbackRetry: "Try again",
    },
    whatISee: "What I see",
    sketchMoreBrands: "+{{count}} more brands",
    sketchMoreBrands_one: "+{{count}} more brand",
    sketchEnlarge: "Enlarge {{label}}",
    whatISeeCount: "What I see ({{count}})",
    moreSettings: "More settings",
    moreStores: "{{count}} more stores",
    technicalError: "Technical error for {{store}}",
    copiedBase: "Copied base",
    currentPlan: "Current plan for the remaining stores",
    blockerInDetails: "Generation has a blocking issue. Review it under Details before generating.",
    copyMatchingSummary:
      "Copy the base layout and range to {{copied}} matching stores; lay out {{fresh}} other stores fresh using Advanced settings.",
    freshSummary: "Lay out all {{count}} stores fresh using Advanced settings.",
    layoutPendingSummary:
      "{{eligible}} of {{total}} stores may match the base for copying; choose how to lay out the stores.",
    copyRangeCost:
      "{{count}} matching fixtures have a different range; copying uses the base range there.",
    copyOverrideCost: "Copying overrides {{reason}} in {{count}} stores.",
    summarySet: "Set {{label}} to {{value}} with O{{detail}}",
    summaryConfirmed: "Confirmed {{label}} is {{value}} with O{{detail}}",
    summaryPolicySet: "O set: {{policy}}{{detail}}",
    summaryPolicyConfirmed: "Confirmed with O: {{policy}}{{detail}}",
    recommendedChoice: "O recommends this choice",
    currentChoice: "Current choice",
    choiceCost: "Tradeoff: {{cost}}",
    preferFullShelves: "Prefer full shelves",
    allowShelfGaps: "Do not penalize shelf gaps",
    allowShelfMoves: "Allow shelf-height changes",
    keepShelfHeights: "Keep shelf heights fixed",
    rankSales: "Rank by sales",
    rankProfit: "Rank by profit",
    useReleasedCdt: "Use the CDT Studio release",
    useStoreLabels: "Use store product labels",
    settingOn: "on",
    settingOff: "off",
    settingUnset: "not set",
    settingCustom: "custom value",
    summaryKept: "Kept {{label}}: {{reason}}",
    summaryLimit: "Generation limit: {{line}}",
    whatISet: "What I set",
    anythingToChange: "Anything you'd change?",
    kept: "You chose {{yours}}; I'd pick {{oWould}}: {{reason}}",
    keptNoReason: "You chose {{yours}}; I'd pick {{oWould}}",
    runAgain: "Run again",
    openStorePlanograms: "Open Store Planograms",
    close: "Close",
  },
  aisleBreaks: {
    and: "and",
    or: "or",
    listMore: "{{list}} and {{count}} more",
    suggestion: {
      gap: "Bays {{left}} and {{right}} are {{gap}} {{unit}} apart. Treated as one aisle.",
      gapWithoutUnit: "Bays {{left}} and {{right}} are {{gap}} apart. Treated as one aisle.",
      units: {
        inches: "in",
        centimetres: "cm",
      },
      unmergedSeam:
        "No shelf continues from bay {{left}} into bay {{right}}. Treated as one aisle.",
      breakHere: "Break the aisle here",
      declared: "Aisle break after bay {{bayNo}}. Set by you just now.",
      failed: "The aisle break after bay {{bayNo}} wasn't saved.",
      loading: "Checking the store's fixtures for aisle breaks...",
      loadFailed: "Couldn't check the store's fixtures for aisle breaks.",
      retry: "Retry",
    },
    result: {
      noBrandCrossesOne: "No brand crosses the break after bay {{bayNo}}.",
      noBrandCrossesEither: "No brand crosses either break (after bays {{bayNos}}).",
      noBrandCrossesAny: "No brand crosses any break (after bays {{bayNos}}).",
      noBrandCrossesThese: "No brand crosses the breaks after bays {{bayNos}}.",
      brandOnBothSides:
        "{{brand}} is on both sides of the break after bay {{bayNo}}: {{left}} and {{right}}.",
      brandOnBothSidesPlain: "{{brand}} is on both sides of the break after bay {{bayNo}}.",
      bayCouldNotHoldOne: "Bay {{bayNo}} couldn't hold 1 product: {{products}}.",
      bayCouldNotHoldMany: "Bay {{bayNo}} couldn't hold {{count}} products: {{products}}.",
      baysCouldNotHoldOne: "Bays {{first}}–{{last}} couldn't hold 1 product: {{products}}.",
      baysCouldNotHoldMany:
        "Bays {{first}}–{{last}} couldn't hold {{count}} products: {{products}}.",
      bayHasNo: "Bay {{bayNo}} has no {{sections}}.",
      baysHaveNo: "Bays {{first}}–{{last}} have no {{sections}}.",
      movedAfter: "{{what}} moved after {{after}} so brands fit between the breaks.",
      movedToFront: "{{what}} moved to the front so brands fit between the breaks.",
      movedToFrontOf: "{{what}} moved to the front of {{within}} so brands fit between the breaks.",
      editedAfterGeneration: "Edited after generation: brands may cross the break.",
      columnHeader: "Aisle breaks",
    },
    brandLevel: {
      unresolvedOne:
        "1 store has an aisle break, and this category's CDT doesn't say which level is the brand.",
      unresolvedMany:
        "{{count}} stores have an aisle break, and this category's CDT doesn't say which level is the brand.",
      setBrandLevel: "Set brand level",
      question: "Which of these is the brand?",
      noLevels: "This category's CDT has nothing to choose from.",
      save: "Save",
      cancel: "Cancel",
      saved: "{{name}} is the brand.",
      generateOne: "Generate this store",
      generateMany: "Generate these {{count}} stores",
      noStudioProfile: "This category has no CDT Studio profile yet.",
      noAttributeNamesLevel:
        "CDT Studio has nothing that names only {{name}}, so it can't be saved as the brand.",
      changed: "This category's CDT changed. Generate the stores again to choose.",
      failed: "The brand wasn't saved.",
      runReadFailed: "Couldn't load every refused store.",
      retry: "Retry",
    },
  },
  products: {
    palletEligible: "Pallet Eligible",
    yes: "Yes",
    no: "No",
  },
  primaryBlocking: {
    label: "Primary Blocking:",
    vertical: "Vertical",
    horizontal: "Horizontal",
    release: "studio release {{releaseId}}",
    notFinalized: "studio not finalized",
    tooltipVertical:
      "Store planograms default to vertical blocking, measured against the CDTs finalized in CDT Studio.",
    tooltipRows:
      "Store planograms default to horizontal blocking: CDT Studio finalized this wall's CDTs for horizontal blocking.",
    tooltipNotFinalized:
      "Store planograms default to horizontal blocking. Finalize CDTs in CDT Studio to use vertical blocking.",
  },
  tour: {
    start: "Tour",
    loadFlowsFailed: "Couldn't load your flows for the exercise",
    offer: {
      title: "New here?",
      body: "Take a three-minute exercise on one of your own planograms: reading it, using CDT Studio to make store planograms follow it, and setting the rules to generate them. Nothing is saved or generated. You can also start it later from Tour at the top of any base planogram.",
      dismiss: "Not now",
      start: "Start the exercise",
    },
    card: {
      yourTurn: "Your turn",
      end: "End tour",
      close: "Close",
      back: "Back",
      next: "Next",
      skip: "Skip this step",
      start: "Start",
    },
    steps: {
      welcome: {
        title: "Learn the planogram screen",
        body: "This takes about three minutes on one of your own planograms. You will try the main features yourself, including CDT Studio, which is how store planograms are made to look like the base planogram. Nothing you do in the exercise is saved or generated.",
      },
      "flow-header": {
        title: "Which planogram you are on",
        body: "The header names the retailer, the category, the flow and the base planogram. Check it first when you have several flows open.",
      },
      "primary-blocking": {
        title: "Primary blocking mode",
        body: "How store planograms will be blocked by default. Vertical blocking, the way this planogram is blocked, needs the CDT finalized in CDT Studio. Horizontal blocking runs brands along the shelf.",
      },
      "flow-steps": {
        title: "Where you are in the flow",
        body: "A flow moves from mudmaps to CDT Studio, the base planogram and then store planograms. Completed steps are links, so you can go back to any of them.",
      },
      planogram: {
        title: "The base planogram",
        body: "This is the planogram every store planogram starts from. Drag products to move them, scroll to zoom and select a product to see its details.",
      },
      sidepanel: {
        title: "Tools for the planogram",
        body: "Each tab opens a tool beside the planogram: unranged items, highlights, space mix, fixtures and CDT Studio.",
      },
      "open-studio": {
        title: "Open CDT Studio",
        body: "CDT Studio is where you make store planograms look like this base planogram. It reads how this planogram is laid out, which brands stand together, in what order and with how much space, and turns that into the CDT every store planogram is generated from.",
        instruction: "Click the CDT Studio tab.",
      },
      "studio-tree": {
        title: "The tree is this planogram, written down",
        body: "Each node is a group of products as they stand on this planogram, in the order they stand in. Store planograms follow this tree, so a store keeps the same groups in the same order even on a wall of a different size.",
      },
      "studio-findings": {
        title: "Exceptions: where the base breaks its own pattern",
        body: "An exception is a place where this planogram breaks its own grouping, such as a product standing inside another brand's block. Left alone, it is copied into every store planogram. Each one can be fixed, accepted or dismissed here.",
      },
      "studio-fix-blocking": {
        title: "Fix vertical blocking",
        body: "Squares the brand blocks for clean vertical blocking and previews the result, so every store gets tidy vertical blocks like these rather than a staircase. It changes nothing until you finalize.",
      },
      "studio-finalize": {
        title: "Finalize: make the stores follow this planogram",
        body: "Finalizing saves the CDT as a release, together with what the studio measured here: each block's width, facings and position. Store planograms are then laid out against this planogram instead of the category defaults, and default to vertical blocking, or horizontal if the studio reads this wall as horizontal blocking. Generate Store Planograms then opens with this release selected. Until you finalize, they default to horizontal blocking.",
      },
      "close-studio": {
        title: "Leave the studio",
        body: "Closing without finalizing discards what you did in the studio. Store planograms will not follow this planogram until you come back and finalize.",
        instruction: "Click Close.",
      },
      "discard-studio": {
        title: "Discard what you did in the studio",
        body: "The studio asks before it throws away changes that were not finalized. In the exercise, discard them.",
        instruction: "Click Discard and close.",
      },
      metrics: {
        title: "How good the planogram is",
        body: "These scores update as you edit: return on space, core range, shelf alignment, days of supply and aesthetics. Hover a score to see what it measures.",
      },
      "open-rules": {
        title: "Set the rules for store planograms",
        body: "Generating always starts with the rules. Opening them does not start anything.",
        instruction: "Click Generate Store Planograms.",
      },
      "open-advanced": {
        title: "O first, then every rule",
        body: "The rules open on O, which reads this base planogram and your rules and suggests what to change. Advanced settings shows every rule.",
        instruction: "Click Advanced settings.",
      },
      "rules-without-studio": {
        title: "Finalize CDTs first",
        body: "CDT Studio was not finalized for this planogram, so store planograms cannot be generated yet: they are built from the rules the Studio ships. The button takes you to the studio.",
      },
      "try-vertical": {
        title: "Try vertical blocking",
        body: "See what happens when you ask for vertical blocking without finalized CDTs.",
        instruction: "Switch “Use vertical blocking mode?” to Yes.",
      },
      "stay-horizontal": {
        title: "A vertical run needs finalized CDTs",
        body: "Generation is disabled until you either finalize CDTs in the studio or go back to horizontal.",
        instruction: "Click Stay horizontal.",
      },
      "rules-tabs": {
        title: "The rules, by topic",
        body: "General covers fixtures, range and merchandising. The Auto-Mudmap Engine tab plans the blocks. Optimization Options sets how strictly each rule is held.",
      },
      "rules-footer": {
        title: "What to optimize",
        body: "Choose the CDT level the blocks are planned at and whether to optimize on sales, profit or dispersion. Import and Export save a set of rules as a file.",
      },
      "rules-generate": {
        title: "Start the generation",
        body: "This starts one planogram per store and takes you to Store Planograms, where they appear as they finish. It is switched off during the exercise.",
      },
      "close-rules": {
        title: "Close the rules",
        body: "Cancelling leaves everything as it was.",
        instruction: "Click Cancel.",
      },
      done: {
        title: "That is the whole flow",
        body: "Open CDT Studio, finalize the CDT so the stores follow this planogram, set the rules and generate. You can run this exercise again at any time from Tour at the top of this page.",
      },
      "no-planogram": {
        title: "You need a planogram first",
        body: "The exercise runs on one of your own base planograms, and none of your flows has one yet. Create a flow with New Flow on this page, then start the exercise from Tour at the top of its base planogram.",
      },
    },
  },
} as const;

export type CommonTranslations = typeof commonEn;
